import { Link as RouterLink } from 'react-router-dom';
import { Tooltip, Typography, makeStyles } from '@material-ui/core';
import type { Activity, ActivityHandoffProjection } from '../../api/types';
import { ControlPlaneHandoffSummary } from './ControlPlaneStateCells';

const useStyles = makeStyles(theme => ({
  handoff: {
    display: 'grid',
    gap: theme.spacing(0.25),
    justifyItems: 'start',
  },
  title: {
    display: 'grid',
    gap: theme.spacing(0.5),
    minWidth: 0,
    overflow: 'hidden',
  },
  titleLink: {
    display: 'block',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  stacked: {
    display: 'grid',
    gap: theme.spacing(0.25),
    minWidth: 0,
    overflow: 'hidden',
  },
  truncated: {
    display: 'block',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  secondary: { color: theme.palette.text.secondary },
}));

function handoffLabel(handoff: ActivityHandoffProjection): string {
  switch (handoff.sessionStatus) {
    case 'completed':
      return 'Concluído';
    case 'succeeded':
      return 'Concluído (compatibilidade)';
    case 'revoked':
      return 'Revogado';
    case 'expired':
      return 'Expirado';
    case 'failed':
      return 'Falhou';
    default:
      break;
  }
  const labels: Record<ActivityHandoffProjection['state'], string> = {
    'not-applicable': 'Não aplicável',
    'awaiting-local-onboarding': 'Aguardando conexão',
    'onboarding-in-progress': 'Em conexão',
    connected: 'Conectado',
    'handoff-failed': 'Falhou',
  };
  return labels[handoff.state];
}

function LegacyHandoffSummary({ activity }: { activity: Activity }) {
  const classes = useStyles();
  if (!activity.handoff) {
    return <Typography aria-label="Handoff sem projeção">—</Typography>;
  }
  return (
    <div className={classes.handoff}>
      <Typography variant="caption">
        {handoffLabel(activity.handoff)}
      </Typography>
      {activity.handoff.alert?.code === 'HANDOFF_CONNECTION_STALLED' && (
        <Typography
          component="span"
          variant="caption"
          color="error"
          role="alert"
        >
          Conexão atrasada — verifique o agente.
        </Typography>
      )}
    </div>
  );
}

function CompactLine(props: {
  label: string;
  value: string;
  secondary?: boolean;
}) {
  const classes = useStyles();
  return (
    <Tooltip title={`${props.label}: ${props.value}`} arrow>
      <Typography
        component="span"
        variant={props.secondary ? 'caption' : 'body2'}
        className={`${classes.truncated} ${
          props.secondary ? classes.secondary : ''
        }`}
        aria-label={`${props.label}: ${props.value}`}
      >
        {props.value}
      </Typography>
    </Tooltip>
  );
}

export function ActivityTitleCell(props: {
  activity: Activity;
  locked: boolean;
  route: string;
}) {
  const classes = useStyles();
  const title = props.locked ? (
    <Tooltip
      title="Atividade pertence a outro usuário. Acesso liberado apenas após a conclusão."
      arrow
    >
      <span className={classes.titleLink} style={{ opacity: 0.55 }}>
        {props.activity.title}
      </span>
    </Tooltip>
  ) : (
    <Tooltip title={props.activity.title} arrow>
      <RouterLink className={classes.titleLink} to={props.route}>
        {props.activity.title}
      </RouterLink>
    </Tooltip>
  );
  return (
    <div className={classes.title}>
      {title}
      {props.activity.controlPlane ? (
        <ControlPlaneHandoffSummary
          projection={props.activity.controlPlane}
          stalled={
            props.activity.handoff?.alert?.code === 'HANDOFF_CONNECTION_STALLED'
          }
        />
      ) : (
        <LegacyHandoffSummary activity={props.activity} />
      )}
    </div>
  );
}

export function DestinationCell(props: {
  activity: Activity;
  projectLabel: string;
}) {
  const classes = useStyles();
  return (
    <div className={classes.stacked}>
      <CompactLine label="Projeto" value={props.projectLabel} />
      <CompactLine
        label="Repositório"
        value={props.activity.repoName ?? props.activity.azureRepository}
        secondary
      />
    </div>
  );
}

export function CreationCell(props: { activity: Activity; userLabel: string }) {
  const classes = useStyles();
  return (
    <div className={classes.stacked}>
      <CompactLine label="Criado por" value={props.userLabel} />
      <CompactLine
        label="Criado em"
        value={new Date(props.activity.createdAt).toLocaleDateString('pt-BR')}
        secondary
      />
    </div>
  );
}
